<template>
  <div class="lc-list-view">
    <div class="page-header">
      <h2>LC(신용장) 관리</h2>
      <div class="header-actions">
        <!-- ag-grid 전환 (2026-07-12): 상태/법인/만료 필터는 그리드 컬럼 필터로 -->
        <el-button :type="showAll ? '' : 'warning'" @click="toggleShowAll" size="small">
          {{ showAll ? '스마트뷰' : '전체보기' }}
        </el-button>
        <el-button
          v-if="userStore.isLCManager"
          type="primary"
          @click="openCreateDialog"
        >
          <el-icon><Plus /></el-icon>
          신규 LC
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <div class="ag-theme-alpine lc-grid" style="height: 620px; width: 100%;" v-loading="loading">
      <ag-grid-vue
        style="width: 100%; height: 100%;"
        :columnDefs="columnDefs"
        :rowData="lcs"
        :defaultColDef="defaultColDef"
        @row-clicked="onRowClicked"
      ></ag-grid-vue>
      <div class="grid-hint">행 클릭 → LC 상세 · 스마트뷰 = 결제완료/취소 제외 · 컬럼 헤더에서 필터/정렬</div>
    </div>
    <el-empty v-if="!loading && lcs.length === 0" description="등록된 LC가 없습니다" :image-size="70" />

    <!-- 생성 다이얼로그 -->
    <LCFormDialog
      v-model="showCreateDialog"
      @saved="handleSaved"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/lc-list'"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { Plus, QuestionFilled } from '@element-plus/icons-vue'
import { AgGridVue } from 'ag-grid-vue3'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import LCFormDialog from '@/components/trade/LCFormDialog.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const router = useRouter()
const userStore = useUserStore()
const codeStore = useCodeStore()

const loading = ref(false)
const lcs = ref([])
const helpVisible = ref(false)
const showCreateDialog = ref(false)
const showAll = ref(false)

const formatNumber = (num) => {
  const n = Number(num)
  if (num === null || num === undefined || num === '' || Number.isNaN(n)) return '0'
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const daysUntil = (dateStr) => {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr) - new Date(new Date().toDateString())) / 86400000)
}
const ddayLabel = (d) => (d == null ? '' : (d < 0 ? `D+${Math.abs(d)}` : `D-${d}`))

// ── ag-grid ──
const defaultColDef = { resizable: true, sortable: true, filter: true, floatingFilter: true, minWidth: 90 }
const numCol = { cellClass: 'num-cell', filter: 'agNumberColumnFilter' }
const columnDefs = ref([
  { headerName: 'LC번호', field: 'lc_number', width: 160, filter: 'agTextColumnFilter', pinned: 'left' },
  { headerName: '법인', field: 'company_name', width: 110 },
  // 지결번호 (2026-07-21, 김기명 제보) — 연결된 지출결의서 번호 (다건이면 쉼표 나열)
  { headerName: '지결번호', field: 'expense_voucher_number', width: 130, filter: 'agTextColumnFilter',
    valueGetter: (p) => {
      const nums = p.data?.voucher_numbers || []
      return nums.length ? nums.join(', ') : (p.data?.expense_voucher_number || '')
    } },
  { headerName: '유형', field: 'lc_type_display', width: 130 },
  { headerName: '상태', field: 'status_display', width: 105,
    cellClassRules: {
      'lc-danger': (p) => ['취소'].includes(p.value) || p.data?.is_expired,
      'lc-active': (p) => ['선적완료', '매입완료'].includes(p.value),
    } },
  { headerName: '통화', field: 'currency', width: 80 },
  { headerName: '금액', field: 'amount', width: 130, ...numCol, valueFormatter: (p) => formatNumber(p.value) },
  { headerName: '개설은행', field: 'issuing_bank', width: 120, filter: 'agTextColumnFilter' },
  { headerName: '수익자', field: 'beneficiary', width: 130, filter: 'agTextColumnFilter' },
  { headerName: '개설일', field: 'issue_date', width: 110 },
  { headerName: '상환만기', field: 'payment_due_date', width: 145, sort: 'asc',
    valueFormatter: (p) => (p.value ? `${p.value} ${ddayLabel(daysUntil(p.value))}` : ''),
    cellClassRules: {
      'dday-over': (p) => p.value && daysUntil(p.value) < 0 && !['SETTLED', 'CANCELLED'].includes(p.data?.status),
      'dday-soon': (p) => p.value && daysUntil(p.value) >= 0 && daysUntil(p.value) <= 7,
    } },
  { headerName: '유효기일', field: 'expiry_date', width: 145,
    valueFormatter: (p) => {
      if (!p.value) return ''
      if (p.data?.is_expired) return `${p.value} 만료`
      const d = p.data?.days_until_expiry
      return d != null ? `${p.value} D-${d}` : p.value
    },
    cellClassRules: {
      'dday-over': (p) => !!p.data?.is_expired,
      'dday-soon': (p) => !p.data?.is_expired && p.data?.days_until_expiry != null && p.data.days_until_expiry <= 7,
    } },
  { headerName: '메인오더', field: 'main_request_number', width: 140, filter: 'agTextColumnFilter' },
])
const onRowClicked = (e) => router.push(`/lcs/${e.data.id}`)

const fetchData = async () => {
  loading.value = true
  try {
    const params = { page_size: 1000 }
    // 스마트뷰: 결제완료/취소 제외 (기존 동작 유지)
    if (!showAll.value) {
      params.status__in = ['DRAFT', 'REQUESTED', 'ISSUED', 'AMENDED', 'SHIPPED', 'NEGOTIATED'].join(',')
    }
    const response = await axiosInstance.get('lcs/', { params })
    lcs.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch LCs:', error)
  } finally {
    loading.value = false
  }
}

const toggleShowAll = () => {
  showAll.value = !showAll.value
  fetchData()
}

const openCreateDialog = () => {
  showCreateDialog.value = true
}

const handleSaved = () => {
  showCreateDialog.value = false
  fetchData()
}

const showHelp = () => {
  helpVisible.value = true
}

onMounted(() => {
  fetchData()
  codeStore.fetchOurCompanys()
})
</script>

<style scoped>
.lc-list-view {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.lc-grid {
  margin-bottom: 26px;
}

.lc-grid :deep(.ag-row) {
  cursor: pointer;
}

.grid-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

:deep(.num-cell) {
  text-align: right;
}

:deep(.dday-soon) {
  color: #e6a23c;
  font-weight: 600;
}

:deep(.dday-over) {
  color: #f56c6c;
  font-weight: 600;
}

:deep(.lc-danger) {
  color: #f56c6c;
}

:deep(.lc-active) {
  color: #67c23a;
}
</style>
